import { useState, useEffect, useRef } from "react";
import { X, ShieldCheck, ShieldAlert, RefreshCw, Lock, Ban, Server, PhoneOff, Info, History, KeyRound, Copy, Database } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const Badge = ({ ok, okText, badText }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${ok ? "bg-emerald-900/40 text-emerald-300 border border-emerald-700/50" : "bg-red-900/40 text-red-300 border border-red-700/50"}`}>
    {ok ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}{ok ? okText : badText}
  </span>
);

export default function SecurityPanel({ onClose }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [enc, setEnc] = useState(null);
  const [genKey, setGenKey] = useState("");
  const [keyInput, setKeyInput] = useState("");
  const [showEncHelp, setShowEncHelp] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const restoreRef = useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/security/status"), api.get("/security/events").catch(() => ({ data: [] })), api.get("/security/encryption-status").catch(() => ({ data: null }))])
      .then(([s, e, en]) => { setData(s.data); setEvents(e.data || []); setEnc(en.data); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const generateKey = async () => {
    try { const { data: d } = await api.post("/security/generate-encryption-key"); setGenKey(d.key); setKeyInput(d.key); toast.success(t("Wygenerowano nowy klucz — skopiuj, zapisz i kliknij „Aktywuj klucz”")); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd")); }
  };
  const copyKey = () => { navigator.clipboard?.writeText(genKey || keyInput); toast.success(t("Skopiowano klucz do schowka")); };
  const activateKey = async () => {
    const k = (keyInput || genKey || "").trim();
    if (k.length < 16) { toast.error(t("Klucz zbyt krótki (min. 16 znaków).")); return; }
    try { const { data: d } = await api.post("/security/set-encryption-key", { key: k }); toast.success(t("Klucz aktywowany") + (d.key_fingerprint ? ` (${d.key_fingerprint})` : "")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd aktywacji klucza")); }
  };
  const downloadBackup = async () => {
    setBackupBusy(true);
    try {
      const res = await api.get("/security/backup", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `fiberpass-backup-${new Date().toISOString().slice(0, 10)}.fpenc`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t("Pobrano zaszyfrowaną kopię bazy"));
    } catch (e) {
      let msg = e.response?.data?.detail || t("Błąd tworzenia kopii");
      if (e.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* noop */ } }
      toast.error(msg);
    } finally { setBackupBusy(false); }
  };
  const onRestoreFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!window.confirm(t("UWAGA: Przywrócenie kopii NADPISZE całą obecną bazę danymi z pliku. Tej operacji nie można cofnąć. Kontynuować?"))) return;
    setRestoreBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: d } = await api.post("/security/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Przywrócono kopię") + `: ${d.collections} ${t("kolekcji")}, ${d.total} ${t("rekordów")}`);
      setTimeout(() => window.location.reload(), 1800);
    } catch (e2) { toast.error(e2.response?.data?.detail || t("Błąd przywracania")); }
    finally { setRestoreBusy(false); }
  };

  const unban = async (ip) => {
    setBusy(ip);
    try { await api.post("/security/fail2ban/unban", { ip }); toast.success(`${t("Odbanowano")} ${ip}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd")); }
    finally { setBusy(""); }
  };

  const ufw = data?.ufw;
  const voip = data?.voip;
  const f2b = data?.fail2ban;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4" data-testid="security-panel">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><ShieldCheck size={18} className="text-cyan-400" /> {t("Bezpieczeństwo serwera")}</h3>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="security-refresh" className="text-slate-400 hover:text-white p-1"><RefreshCw size={16} /></button>
            <button onClick={onClose} data-testid="security-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          {loading ? <div className="text-slate-500 text-sm">{t("Ładowanie…")}</div> : !data ? (
            <div className="text-red-400 text-sm">{t("Nie udało się pobrać statusu (wymaga roli administratora).")}</div>
          ) : (
            <>
              {data.container_note && (
                <div className="flex gap-2 text-[11px] text-slate-400 bg-slate-900/60 border border-slate-700 rounded p-2" data-testid="security-note">
                  <Info size={14} className="shrink-0 mt-0.5 text-slate-500" /> {data.container_note}
                </div>
              )}

              {data.alerts?.length > 0 && (
                <div className="rounded-lg border border-red-700/50 bg-red-950/30 p-3" data-testid="security-alerts">
                  <div className="text-sm font-heading font-bold text-red-300 mb-1 flex items-center gap-2"><ShieldAlert size={15} /> {t("Wykryte problemy")}</div>
                  <ul className="text-xs text-red-200 list-disc pl-5 space-y-0.5">
                    {data.alerts.map((a, i) => <li key={i}>{a.message}</li>)}
                  </ul>
                  <p className="text-[10px] text-slate-400 mt-1">{t("Administratorzy otrzymują powiadomienie push, gdy pojawi się nowy problem.")}</p>
                </div>
              )}

              {/* Szyfrowanie bazy danych (at-rest) */}
              <div className="rounded-lg border border-cyan-800/50 bg-cyan-950/20 p-4" data-testid="security-encryption">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-heading font-bold flex items-center gap-2"><Database size={15} className="text-cyan-400" /> {t("Szyfrowanie bazy danych (at-rest)")}</div>
                  <Badge ok={!!enc?.key_configured} okText={t("klucz aktywny")} badText={t("brak klucza")} />
                </div>
                <p className="text-[12px] text-slate-400 mb-2">
                  {t("Zabezpiecza wszystkie pliki bazy (dane, indeksy, kopie) na wypadek kradzieży dysku lub backupu. Klucz przechowywany jest wyłącznie poza bazą.")}
                  {enc?.key_fingerprint && <span className="ml-1 text-slate-500">{t("Odcisk klucza")}: <b className="font-mono text-slate-300">{enc.key_fingerprint}</b></span>}
                </p>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <button onClick={generateKey} data-testid="enc-generate-key" className="inline-flex items-center gap-1.5 bg-cyan-700 hover:bg-cyan-600 text-white text-xs font-semibold rounded px-3 py-1.5"><KeyRound size={13} /> {t("Generuj klucz")}</button>
                  {genKey && <button onClick={copyKey} data-testid="enc-copy-key" className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs rounded px-3 py-1.5"><Copy size={13} /> {t("Kopiuj")}</button>}
                  <button onClick={() => setShowEncHelp((v) => !v)} data-testid="enc-toggle-help" className="text-xs text-cyan-300 hover:text-cyan-200 underline">{showEncHelp ? t("Ukryj instrukcję") : t("Pokaż instrukcję wdrożenia")}</button>
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <input value={keyInput} onChange={(e) => setKeyInput(e.target.value)} data-testid="enc-key-input"
                    placeholder={t("Wklej klucz (nowy lub istniejący, by przywrócić kopię)")}
                    className="flex-1 min-w-[220px] bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs font-mono text-cyan-200" />
                  <button onClick={activateKey} data-testid="enc-activate-key" className="inline-flex items-center gap-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold rounded px-3 py-1.5"><KeyRound size={13} /> {t("Aktywuj klucz")}</button>
                </div>
                {!enc?.key_configured && (
                  <div className="text-[11px] text-amber-300 bg-amber-950/20 border border-amber-800/40 rounded p-2 mb-2" data-testid="enc-no-key-hint">
                    {t("Aby pobierać/przywracać zaszyfrowaną kopię, wygeneruj klucz i kliknij „Aktywuj klucz” (lub ustaw ENCRYPTION_KEY w backend/.env).")}
                  </div>
                )}
                <div className="mb-2">
                  <button onClick={downloadBackup} disabled={backupBusy || !enc?.key_configured} data-testid="enc-download-backup"
                    className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-semibold rounded px-3 py-1.5">
                    <Database size={13} /> {backupBusy ? t("Tworzenie kopii…") : t("Pobierz zaszyfrowaną kopię bazy")}
                  </button>
                  <span className="ml-2 text-[11px] text-slate-500">{t("Plik .fpenc — czytelny tylko z tym samym kluczem. Trzymaj poza serwerem.")}</span>
                  <div className="mt-2">
                    <input ref={restoreRef} type="file" accept=".fpenc" onChange={onRestoreFile} className="hidden" data-testid="enc-restore-input" />
                    <button onClick={() => restoreRef.current?.click()} disabled={restoreBusy || !enc?.key_configured} data-testid="enc-restore-backup"
                      className="inline-flex items-center gap-1.5 bg-amber-700 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-semibold rounded px-3 py-1.5">
                      <RefreshCw size={13} /> {restoreBusy ? t("Przywracanie…") : t("Przywróć z kopii (.fpenc)")}
                    </button>
                    <span className="ml-2 text-[11px] text-amber-400">{t("Uwaga: nadpisuje całą obecną bazę.")}</span>
                  </div>
                </div>
                {genKey && (
                  <div className="bg-slate-950 border border-cyan-700/40 rounded p-2 mb-2" data-testid="enc-key-box">
                    <div className="text-[10px] text-slate-500 uppercase mb-1">{t("Nowy klucz (skopiuj i zapisz — nie będzie pokazany ponownie)")}</div>
                    <code className="text-[12px] text-cyan-200 break-all font-mono select-all">{genKey}</code>
                  </div>
                )}
                <div className="flex gap-2 text-[11px] text-amber-300 bg-amber-950/20 border border-amber-800/40 rounded p-2">
                  <ShieldAlert size={14} className="shrink-0 mt-0.5" /> {t("Utrata klucza oznacza trwałą, nieodwracalną utratę danych. Zrób kopię offline.")}
                </div>
                {showEncHelp && (
                  <pre className="text-[11px] bg-slate-950 border border-slate-700 rounded p-2 mt-2 overflow-x-auto text-slate-300 whitespace-pre-wrap">{`# LUKS/dm-crypt na wolumenie danych MongoDB (Debian/Ubuntu)
sudo apt-get install -y cryptsetup
printf '%s' 'WKLEJ_KLUCZ_Z_APLIKACJI' | sudo tee /root/fiberpass.key >/dev/null && sudo chmod 600 /root/fiberpass.key
sudo cryptsetup luksFormat --type luks2 /dev/sdb /root/fiberpass.key
sudo cryptsetup luksOpen /dev/sdb fiberpass_data --key-file /root/fiberpass.key
sudo mkfs.ext4 /dev/mapper/fiberpass_data
sudo mkdir -p /var/lib/mongodb-enc && sudo mount /dev/mapper/fiberpass_data /var/lib/mongodb-enc
# MIGRACJA istniejących danych na zaszyfrowany dysk:
sudo systemctl stop mongod
sudo rsync -a /var/lib/mongodb/ /var/lib/mongodb-enc/ && sudo chown -R mongodb:mongodb /var/lib/mongodb-enc
# ustaw storage.dbPath: /var/lib/mongodb-enc w /etc/mongod.conf
sudo systemctl start mongod
# Pełna instrukcja: backend/ENCRYPTION_AT_REST.md`}</pre>
                )}
              </div>

              {/* Firewall */}
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-heading font-bold flex items-center gap-2"><Lock size={15} className="text-cyan-400" /> {t("Zapora (UFW)")}</div>
                  {ufw?.available
                    ? <Badge ok={ufw.active} okText={t("aktywna")} badText={t("nieaktywna")} />
                    : <span className="text-xs text-slate-500">{t("niedostępna na tym hoście")}</span>}
                </div>
                {ufw?.available && (
                  <div className="text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
                    <span>{t("Domyślnie przychodzące")}: <Badge ok={ufw.default_deny_incoming} okText="deny" badText={t("otwarte")} /></span>
                    <span>{t("Reguły")}: <b className="text-slate-300 font-mono">{ufw.rules?.length || 0}</b></span>
                  </div>
                )}
              </div>

              {/* VoIP */}
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-4" data-testid="security-voip">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-heading font-bold flex items-center gap-2"><PhoneOff size={15} className="text-amber-400" /> {t("Porty VoIP / SIP")}</div>
                  <Badge ok={voip?.all_blocked} okText={t("wszystkie zablokowane")} badText={t("część otwarta!")} />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {voip?.ports.map((p) => (
                    <div key={p.port} className={`flex items-center justify-between px-2 py-1 rounded text-xs border ${p.blocked ? "bg-emerald-900/20 border-emerald-800/50 text-emerald-300" : "bg-red-900/25 border-red-700/50 text-red-300"}`} data-testid={`voip-port-${p.port.replace(/[/:]/g, "-")}`}>
                      <span className="font-mono">{p.port}</span>
                      {p.blocked ? <Ban size={12} /> : <ShieldAlert size={12} />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Fail2ban */}
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-heading font-bold flex items-center gap-2"><Server size={15} className="text-cyan-400" /> {t("Fail2ban (ochrona SSH)")}</div>
                  {f2b?.available
                    ? <Badge ok={f2b.sshd_active} okText={t("aktywny")} badText={t("nieaktywny")} />
                    : <span className="text-xs text-slate-500">{t("niedostępny na tym hoście")}</span>}
                </div>
                {f2b?.available && f2b?.sshd_active && (
                  <div className="text-xs text-slate-400 flex flex-wrap gap-x-4">
                    <span>{t("Obecnie zbanowane IP")}: <b className="text-slate-300 font-mono">{f2b.currently_banned}</b></span>
                    <span>{t("Łącznie banów")}: <b className="text-slate-300 font-mono">{f2b.total_banned}</b></span>
                  </div>
                )}
                {f2b?.banned_ips?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="banned-ips">
                    {f2b.banned_ips.map((ip) => (
                      <span key={ip} className="inline-flex items-center gap-1.5 bg-slate-800 border border-slate-700 rounded px-2 py-0.5 text-xs font-mono">
                        {ip}
                        <button onClick={() => unban(ip)} disabled={busy === ip} data-testid={`unban-${ip}`} title={t("Odbanuj")} className="text-red-400 hover:text-red-300 disabled:opacity-40"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Historia zdarzeń bezpieczeństwa */}
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-4" data-testid="security-events">
                <div className="text-sm font-heading font-bold mb-2 flex items-center gap-2"><History size={15} className="text-slate-400" /> {t("Historia zdarzeń bezpieczeństwa")}</div>
                {events.length === 0 ? (
                  <p className="text-xs text-slate-500">{t("Brak zapisanych zdarzeń.")}</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto divide-y divide-slate-800">
                    {events.map((e, i) => (
                      <div key={i} className="flex items-start gap-2 py-1.5 text-xs" data-testid={`security-event-${i}`}>
                        <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${e.kind === "resolved" ? "bg-emerald-400" : e.level === "critical" ? "bg-red-500" : "bg-amber-400"}`} />
                        <span className="text-slate-500 font-mono shrink-0">{(e.ts || "").replace("T", " ").slice(0, 16)}</span>
                        <span className="text-slate-300">{e.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* VoIP secure enable guidance */}
              <div className="rounded-lg border border-amber-700/40 bg-amber-950/20 p-4" data-testid="security-voip-guide">
                <div className="text-sm font-heading font-bold text-amber-300 mb-1 flex items-center gap-2"><Info size={15} /> {t("VoIP — bezpieczne włączenie (gdy wrócimy)")}</div>
                <p className="text-[12px] text-slate-400 mb-2">{t("VoIP jest celowo wyłączone. Gdy będziesz go używać, otwórz porty ręcznie i tylko dla IP operatora:")}</p>
                <pre className="text-[11px] bg-slate-950 border border-slate-700 rounded p-2 overflow-x-auto text-slate-300 whitespace-pre-wrap">{`# 1) Sygnalizacja SIP tylko od operatora (podmień <IP_OPERATORA>)
sudo ufw allow from <IP_OPERATORA> to any port 5060 proto udp
# 2) WebRTC (WSS) dla przeglądarki monterów — najlepiej za proxy 443,
#    ewentualnie bezpośrednio:
sudo ufw allow 8089/tcp
# 3) Media RTP (jeśli wymagane):
sudo ufw allow 10000:10200/udp
sudo ufw reload`}</pre>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
